import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { resolveHref } from '../utils/resolveHref';
import { useCtaColorVars } from '../hooks/useCtaColorVars';
import { onColorLinksCss } from '../utils/onColorLinks';

// ============================================================================
// Meet The Team — Contact Us CTA band
// ---------------------------------------------------------------------------
// Centered closing band for the Meet The Team page. Full-bleed primary color
// with an optional community photo behind it under a heavy overlay — same
// visual vocabulary as LifestyleClosingCTA / BetterBuiltClosing so the page
// closes in the Z Modular voice. Single CTA pushes visitors to /contact-us
// instead of embedding an inline form.
// ============================================================================

const backgroundDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Community exterior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Pick a wide community-wide gallery image — exterior, aerial, courtyard, pool deck, or clubroom — that sells the overall sense of place. Skip bedrooms, bathrooms, closets, and floor-plan diagrams.',
    context:
      'Full-bleed background behind the Meet The Team closing CTA, under a heavy primary-color overlay. Should feel broad and inviting, not a close-up of a single unit.',
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/contact-us',
      suggestedFields: ['property_apply_now_url', 'property_website_url'],
    },
    label: {
      label: 'Label',
      type: 'text',
      defaultValue: 'Contact Us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function MeetTheTeamContactCTA() {
  useRevealStyles();

  const background = useSlotGroup('meettheteamcontactcta.background', backgroundDef);
  const cta = useSlotGroup('meettheteamcontactcta.cta', ctaDef);
  const contentRef = useScrollReveal<HTMLDivElement>();
  const ctaColorVars = useCtaColorVars();

  return (
    <section className="mttc-section" style={ctaColorVars}>
      {/* Background photo + heavy primary overlay. */}
      <div className="mttc-bg">
        <EditableSlotGroup
          groupPath="meettheteamcontactcta.background"
          definition={backgroundDef}
        >
          <div className="absolute inset-0">
            {background.image ? (
              <VibesitesImage
                url={background.image}
                alt={background.alt || ''}
                sizes="100vw"
                className="absolute inset-0 h-full w-full object-cover object-center"
              />
            ) : (
              <div className="absolute inset-0 bg-[var(--vibe-color-primary)]" />
            )}
            <div className="absolute inset-0 bg-[var(--vibe-color-primary)] opacity-[0.85]" />
          </div>
        </EditableSlotGroup>
      </div>

      <div ref={contentRef} className="reveal-fade-up mttc-container">
        <RichTextContent
          path="meettheteamcontactcta.eyebrow"
          label="Eyebrow"
          defaultValue="<p>Let's Connect</p>"
          as="div"
          className="mttc-eyebrow"
        />
        <RichTextContent
          path="meettheteamcontactcta.heading"
          label="Heading"
          defaultValue="<p>Have a Question for Our Team?</p>"
          as="h2"
          className="mttc-heading"
        />
        <RichTextContent
          path="meettheteamcontactcta.subheading"
          label="Subheading"
          defaultValue='<p>Our leasing and management team at <span data-type="field-token" data-field="property_name"></span> is ready to help with tours, availability, or anything else you want to know before you move in.</p>'
          as="div"
          className="mttc-sub"
        />

        <EditableSlotGroup groupPath="meettheteamcontactcta.cta" definition={ctaDef}>
          <a
            href={resolveHref(cta.url || '/contact-us')}
            target={cta._target || '_self'}
            className="mttc-cta"
          >
            {cta.label || 'Contact Us'}
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        ${onColorLinksCss('.mttc-section [data-vibesite-richtext]')}
        .mttc-section {
          position: relative;
          overflow: hidden;
          background: var(--vibe-color-primary);
          padding: 6rem 1.5rem 6rem;
        }
        .mttc-bg {
          position: absolute;
          inset: 0;
        }

        .mttc-container {
          position: relative;
          z-index: 1;
          max-width: 720px;
          margin: 0 auto;
          text-align: center;
          color: #ffffff;
        }

        .mttc-eyebrow,
        .mttc-eyebrow * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .mttc-eyebrow {
          color: rgba(255, 255, 255, 0.78);
          font-size: 0.78rem;
          letter-spacing: 0.32em;
          text-transform: uppercase;
          margin: 0 0 1rem;
        }
        .mttc-eyebrow p { margin: 0; }

        .mttc-heading,
        .mttc-heading * {
          font-family: var(--vibe-font-heading);
          font-weight: 400;
        }
        .mttc-heading {
          font-size: clamp(2.1rem, 5vw, 3.5rem);
          line-height: 1.05;
          letter-spacing: 0.03em;
          margin: 0 auto 1.25rem;
          max-width: 28rem;
        }

        .mttc-sub,
        .mttc-sub * {
          font-family: var(--vibe-font-body);
          font-weight: 400;
        }
        .mttc-sub {
          color: rgba(255, 255, 255, 0.88);
          font-size: 1rem;
          line-height: 1.7;
          max-width: 36rem;
          margin: 0 auto 2.5rem;
        }
        .mttc-sub p { margin: 0 0 0.5rem; }
        .mttc-sub p:last-child { margin-bottom: 0; }

        .mttc-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 1.1rem 2.75rem;
          background: var(--cta-bg, var(--vibe-color-secondary));
          color: var(--cta-text, var(--vibe-color-text-on-secondary, #ffffff));
          border: 1.5px solid var(--cta-bg, var(--vibe-color-secondary));
          font-family: var(--vibe-font-heading);
          font-size: 0.9rem;
          font-weight: 400;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          text-decoration: none;
          transition: background-color 0.25s ease, color 0.25s ease,
                      transform 0.25s ease, border-color 0.25s ease;
        }
        .mttc-cta:hover {
          background: #ffffff;
          color: var(--cta-bg, var(--vibe-color-secondary));
          border-color: #ffffff;
          transform: translateY(-2px);
        }

        @media (max-width: 639px) {
          .mttc-section { padding: 4.5rem 1.25rem 4.5rem; }
          .mttc-cta { width: 100%; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'MeetTheTeamContactCTA',
  displayName: 'Meet The Team — Contact CTA',
  tokens: {
    'meettheteamcontactcta.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: "<p>Let's Connect</p>",
    },
    'meettheteamcontactcta.heading': {
      label: 'Heading',
      type: 'richtext',
      defaultValue: '<p>Have a Question for Our Team?</p>',
    },
    'meettheteamcontactcta.subheading': {
      label: 'Subheading',
      type: 'richtext',
      defaultValue:
        '<p>Our leasing and management team at <span data-type="field-token" data-field="property_name"></span> is ready to help with tours, availability, or anything else you want to know before you move in.</p>',
    },
  },
  slotGroups: {
    'meettheteamcontactcta.background': backgroundDef,
    'meettheteamcontactcta.cta': ctaDef,
  },
};

(MeetTheTeamContactCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default MeetTheTeamContactCTA;
